import ProjectDetails from "@/components/ProjectDetails";
import { useLocation } from "wouter";
import { projects, companies, activities } from '../../../db';
import { logValidationResults } from "@/lib/validation";

export type Project = {
  title: string;
  activityIds: number[]; // Changed to support multiple activities
  activityTitles: string[]; // Array of activity titles
  companyNames: string[]; // Array of company names (could be different if activities span companies)
  company: string; // For backward compatibility with ProjectDetails
  startDate: string;
  endDate: string | null;
  description: string;
  // Legacy format (for backward compatibility)
  skills?: { name: string; weight: number }[];
  // New categorized format
  categorizedSkills?: {
    profession: { name: string; weight: number }[];
    language: { name: string; weight: number }[];
    tools: { name: string; weight: number }[];
    lead: { name: string; weight: number }[];
  };
  goal?: string;
  details?: string;
  detailsFile?: string; // Add support for markdown files
  metrics?: string[];
  role?: string;
  teamSize?: string;
  git?: string;
  link: string;
  status?: string;
  isLinkedProject?: boolean; // Flag to indicate if this appears in multiple activities
  isProfessional?: boolean; // Flag to indicate if this is a professional or private project
};

export const projectsData: Record<string, Project> = projects.reduce((acc, project) => {
  const urlKey = project.title.toLowerCase().replace(/ /g, '-');
  
  // Handle both single activityId (number) and multiple activityIds (array)
  // For private projects, activityId might be null
  const activityIds = project.activityId === null ? [] : 
    (Array.isArray(project.activityId) ? project.activityId : [project.activityId]);
  
  const activitiesList = activityIds.map(id => activities.find(a => a.id === id)).filter(Boolean);
  const companiesList = activitiesList.map(activity => 
    companies.find(c => c.id === activity?.companyId)
  ).filter(Boolean);
  
  acc[urlKey] = {
    title: project.title,
    activityIds: activityIds,
    activityTitles: activitiesList.map(a => a?.title || 'Unknown Activity'),
    companyNames: companiesList.map(c => c?.name || 'Unknown Company'),
    company: companiesList[0]?.name || (project.isProfessional === false ? 'Personal Project' : 'Unknown Company'), // For backward compatibility
    startDate: project.startDate,
    endDate: project.endDate || 'Present',
    description: project.description || '',
    // Include both legacy and new formats for backward compatibility
    skills: 'skills' in project ? project.skills as { name: string; weight: number }[] : undefined,
    categorizedSkills: 'categorizedSkills' in project ? project.categorizedSkills as any : undefined,
    goal: project.goal || '',
    details: 'details' in project ? project.details as string : undefined,
    detailsFile: 'detailsFile' in project ? project.detailsFile as string : undefined,
    metrics: project.metrics || [],
    role: project.role || '',
    teamSize: project.teamSize || '',
    git: project.git || 'not available',
    link: project.link || 'not available',
    status: 'status' in project ? project.status as string : undefined,
    isLinkedProject: activityIds.length > 1,
    isProfessional: project.isProfessional !== undefined ? project.isProfessional : true
  };
  return acc;
}, {} as Record<string, Project>);

// Run validation in development mode
logValidationResults(projects);

export default function ProjectPage({ params }: { params: { id: string } }) {
  const [location, setLocation] = useLocation();
  const project = projectsData[params.id as keyof typeof projectsData];
  
  // Parse activity context from URL search params
  const searchParams = new URLSearchParams(window.location.search);
  const activityId = searchParams.get('activity');
  const activityContext = activityId ? parseInt(activityId) : undefined;

  // Find the original project data to get activityId
  const originalProject = projects.find(p => 
    p.title.toLowerCase().replace(/ /g, '-') === params.id
  );

  if (!project) {
    setLocation("/404");
    return null;
  }

  return (
    <main className="container mx-auto p-6">
      <ProjectDetails 
        project={{
          title: project.title,
          company: project.company,
          startDate: project.startDate,
          endDate: project.endDate || 'Present',
          description: project.description,
          // Pass both legacy and new formats
          skills: project.skills,
          categorizedSkills: project.categorizedSkills,
          goal: project.goal || '',
          details: project.details,
          detailsFile: project.detailsFile,
          metrics: project.metrics || [],
          role: project.role || '',
          teamSize: project.teamSize,
          git: project.git,
          link: project.link,
          status: project.status,
          // Pass the original activityId for skill calculations
          activityId: originalProject?.activityId || null
        }}
        activityId={activityContext}
      />
    </main>
  );
}